// Lightweight API client - does NOT import Zod to avoid TDZ errors in browser bundle
import superjson from 'superjson';

// ============================================
// Types (duplicated from schema files to avoid Zod/Kysely imports)
// ============================================

export type ChatMessage = {
  id: string;
  content: string;
  role: string;
  createdAt: Date | null;
  fileContext: string[] | null;
  imageAttachment: { content: string; name: string } | null;
  userId: number;
  userDisplayName: string;
  userAvatarUrl: string | null;
  checkpointId: string | null;
  personaId: string | null;
  personaName: string | null;
  personaIcon: string | null;
  personaCategory: string | null;
  personaDescription: string | null;
};

export type SessionFile = {
  id: number;
  sessionId: string;
  fileName: string;
  filePath: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
};

export type Checkpoint = {
  id: string;
  sessionId: string;
  name: string;
  description: string | null;
  createdAt: Date;
  checkpointType: string;
  githubRepo: string | null;
  githubBranch: string | null;
  githubCommitSha: string | null;
  githubCommitUrl: string | null;
};

export type CollaborationSession = {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  template: string | null;
  aiEnabled: boolean;
  aiProvider: string | null;
  aiModel: string | null;
  aiCustomEndpoint: string | null;
  aiApiKey: string | null;
  userId: number;
};

// ============================================
// Chat API
// ============================================

export type ChatBody = {
  sessionId: string;
  message: string;
  fileContext?: string[];
  attachments?: Array<{
    data: string;
    name: string;
    addToProject?: boolean;
  }>;
  useLocalLlm?: boolean;
  provider?: string;
  model?: string;
  endpoint?: string;
  apiKey?: string;
  mode?: 'generate' | 'chat' | 'engineer';
  autonomousMode?: boolean;
  currentGoal?: string;
  agentLoop?: boolean;
  maxAgentTurns?: number;
  maxAgentsPerTurn?: number;
  template?: string;
  autoLoop?: boolean;
  maxSteps?: number;
  backgroundMode?: boolean;
  conversationalMode?: boolean;
  maxConversationTurns?: number;
  enabledSkills?: Array<{
    name: string;
    content?: string;
    description?: string;
  }>;
};

export const postChat = async (body: ChatBody, init?: RequestInit): Promise<Response> => {
  const result = await fetch(`/_api/ai/chat`, {
    method: 'POST',
    body: superjson.stringify(body),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    try {
      if (/^\s*</.test(text)) {
        throw new Error(`Request failed with status ${result.status}`);
      }
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (e) {
      if (e instanceof Error && e.message !== 'Unexpected end of JSON input') {
        throw e;
      }
      throw new Error(`Request failed with status ${result.status}`);
    }
  }

  return result;
};

// ============================================
// AI History API
// ============================================

export const getAiHistory = async (
  input: { sessionId: string; limit?: number },
  init?: RequestInit,
): Promise<{ messages: ChatMessage[] }> => {
  // Client-side dedupe/throttle guard:
  // multiple mounted consumers and rapid invalidations can otherwise flood /_api/ai/history.
  // Keep one in-flight request per key and reuse fresh results briefly.
  type CachedHistory = {
    at: number;
    data: { messages: ChatMessage[] };
    inFlight?: Promise<{ messages: ChatMessage[] }>;
  };
  const cacheKey = `${input.sessionId}:${input.limit ?? 'all'}`;
  const historyCache: Map<string, CachedHistory> =
    (globalThis as any).__tasking_ai_history_cache ||
    ((globalThis as any).__tasking_ai_history_cache = new Map<string, CachedHistory>());
  const now = Date.now();
  const cached = historyCache.get(cacheKey);
  const FRESH_MS = 2000;
  if (cached?.data && now - cached.at < FRESH_MS) {
    return cached.data;
  }
  if (cached?.inFlight) {
    return cached.inFlight;
  }

  const params = new URLSearchParams({ sessionId: input.sessionId });
  if (input.limit) params.append('limit', input.limit.toString());

  const request = (async () => {
    const result = await fetch(`/_api/ai/history?${params.toString()}`, {
      method: 'GET',
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!result.ok) {
      const text = await result.text();
      if (!text) {
        throw new Error(`Request failed with status ${result.status}`);
      }
      let errorMsg: string | null = null;
      try {
        const errorObject = superjson.parse<{ error: string }>(text);
        errorMsg = errorObject.error;
      } catch {
        // couldn't parse error body
      }
      throw new Error(errorMsg || `Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
    const data = superjson.parse<{ messages: ChatMessage[] }>(await result.text());
    historyCache.set(cacheKey, { at: Date.now(), data });
    return data;
  })();

  historyCache.set(cacheKey, { at: cached?.at ?? 0, data: cached?.data ?? { messages: [] }, inFlight: request });
  try {
    return await request;
  } finally {
    const latest = historyCache.get(cacheKey);
    if (latest?.inFlight) {
      historyCache.set(cacheKey, { at: latest.at, data: latest.data });
    }
  }
};

// ============================================
// Files API
// ============================================

export const getFilesList = async (
  input: { sessionId: string },
  init?: RequestInit,
): Promise<{ files: SessionFile[] }> => {
  const params = new URLSearchParams({ sessionId: input.sessionId });
  const result = await fetch(`/_api/files/list?${params.toString()}`, {
    method: 'GET',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<{ files: SessionFile[] }>(await result.text());
};

// ============================================
// Checkpoints API
// ============================================

export const createCheckpoint = async (
  body: {
    sessionId: string;
    name: string;
    description?: string;
    checkpointType?: string;
    githubRepo?: string;
    githubBranch?: string;
  },
  init?: RequestInit,
): Promise<{
  checkpoint: Checkpoint;
  githubCommit?: { success: boolean; commitSha?: string; commitUrl?: string; error?: string };
}> => {
  const result = await fetch(`/_api/checkpoints/create`, {
    method: 'POST',
    body: superjson.stringify(body),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<{
    checkpoint: Checkpoint;
    githubCommit?: { success: boolean; commitSha?: string; commitUrl?: string; error?: string };
  }>(await result.text());
};

// ============================================
// AI Config API
// ============================================

export const updateAiConfig = async (
  body: {
    sessionId: string;
    aiEnabled: boolean;
    aiProvider: string;
    aiModel?: string;
    aiCustomEndpoint?: string;
  },
  init?: RequestInit,
): Promise<{ session: CollaborationSession }> => {
  const result = await fetch(`/_api/ai/config`, {
    method: 'POST',
    body: superjson.stringify(body),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<{ session: CollaborationSession }>(await result.text());
};

// ============================================
// Admin API
// ============================================

export type AdminUserView = {
  id: number;
  email: string;
  displayName: string;
  role: 'admin' | 'host' | 'guest';
  createdAt: Date;
};

export const getUsers = async (init?: RequestInit): Promise<{ users: AdminUserView[] }> => {
  const result = await fetch(`/_api/admin/users`, {
    method: 'GET',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<{ users: AdminUserView[] }>(await result.text());
};

export const postResetPassword = async (
  body: { userId: number; newPassword: string },
  init?: RequestInit,
): Promise<{ success: boolean; message: string }> => {
  const result = await fetch(`/_api/admin/reset-password`, {
    method: 'POST',
    body: superjson.stringify(body),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<{ success: boolean; message: string }>(await result.text());
};

// ============================================
// TaskingBot Chat API
// ============================================

export type TaskingBotChatBody = {
  message: string;
  new_task?: boolean; // When true, backend ignores conversation history (slash commands, new tasks)
  history?: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
  }>;
  model?: string;
  skills?: Array<{
    name: string;
    content?: string;
    description?: string;
  }>;
  attachment?: {
    name: string;
    type: string;
    content: string; // base64 data URL
    size?: number;
  };
  attachments?: Array<{
    name: string;
    type: string;
    content: string; // base64 data URL
    size?: number;
  }>;
  multiAgent?: 'parallel' | 'sequential';
  participants?: string[];
  language?: string;
  channel?: 'cloud_browser' | 'extension' | 'desktop';
};

export const postTaskingBotChat = async (
  body: TaskingBotChatBody,
  init?: RequestInit,
): Promise<Response> => {
  const result = await fetch(`/_api/taskingbot/chat`, {
    method: 'POST',
    body: superjson.stringify(body),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    // Try superjson first, then plain JSON, then raw text
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error || `Request failed with status ${result.status}`);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('JSON') && !e.message.includes('parse'))
        throw e;
    }
    try {
      const jsonObj = JSON.parse(text);
      throw new Error(jsonObj.error || `Request failed with status ${result.status}`);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('JSON') && !e.message.includes('parse'))
        throw e;
    }
    throw new Error(text || `Request failed with status ${result.status}`);
  }

  return result;
};

// ============================================
// GitHub API (Zod-free versions)
// ============================================

export type CreateGithubRepoInput = {
  name: string;
  description?: string;
  private?: boolean;
  auto_init?: boolean;
  pat?: string;
};

export type CreateGithubRepoOutput = {
  id: number;
  name: string;
  full_name: string;
  html_url: string;
  default_branch: string;
  private: boolean;
};

export const createGithubRepo = async (
  input: CreateGithubRepoInput,
  init?: RequestInit,
): Promise<CreateGithubRepoOutput> => {
  const result = await fetch(`/_api/github/create-repo`, {
    method: 'POST',
    body: JSON.stringify(input),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<CreateGithubRepoOutput>(await result.text());
};

// ============================================
// Bulk Files API (Zod-free)
// ============================================

export type BulkCreateFilesInput = {
  sessionId: string;
  files: Array<{
    filePath: string;
    fileName: string;
    content: string;
  }>;
};

export type BulkCreateFilesOutput = {
  created: number;
  files: Array<{
    id: number;
    fileName: string;
    filePath: string;
  }>;
};

export const bulkCreateFiles = async (
  input: BulkCreateFilesInput,
  init?: RequestInit,
): Promise<BulkCreateFilesOutput> => {
  const result = await fetch(`/_api/files/bulk-create`, {
    method: 'POST',
    body: superjson.stringify(input),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!result.ok) {
    const text = await result.text();
    if (!text) {
      throw new Error(`Request failed with status ${result.status}`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(text);
      throw new Error(errorObject.error);
    } catch (parseError) {
      throw new Error(`Request failed with status ${result.status}: ${text.substring(0, 100)}`);
    }
  }
  return superjson.parse<BulkCreateFilesOutput>(await result.text());
};
